import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Save } from 'lucide-react';
import { apiClient } from '../../api/apiClient';
import { useToast } from '../../components/Toast';
import SituationWidgetMatrix from './SituationWidgetMatrix';
import SituationWidgetMobileList from './SituationWidgetMobileList';
import { useCompanyNames } from './adminlayout/useCompanyNames';
import {
  stdHeaderRow,
  stdPageTitle,
  stdPageSubtitle,
  stdBlackBtn,
} from './adminlayout/designStandard';

// 2026-06-16 상황판 관리 페이지 오버홀:
//   PC (lg+) = 매트릭스 view (행=위젯, 열=권한, 셀=체크박스, 회사별 그룹 헤더, 즉시 저장 디바운스).
//   모바일/태블릿 (lg 미만) = 기존 좌측 권한 list + 우측 위젯 list (수동 저장 버튼).
//   매트릭스 / 모바일 컴포넌트는 별도 파일 (300줄 규칙).

export default function SituationWidgetManagement() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedPerm, setSelectedPerm] = useState('');
  const [selectedCodes, setSelectedCodes] = useState([]);
  // 매트릭스용: perm_code → widget_code[] 즉시 반영 + 저장 대기 set
  const [matrixMap, setMatrixMap] = useState({});
  // 저장할 때 읽는 최신 칸 상태(2026-10-24 저장 고장 수정). 예전엔 setMatrixMap 갱신 함수 안에서 목록을 꺼냈는데,
  //   React 19(createRoot)에선 그 함수가 저장 요청보다 늦게 돌아 늘 빈 목록이 저장됐다(칸 하나 누르면 그 열쇠 위젯 전부 0개).
  const matrixMapRef = useRef({});
  const [pendingPermCodes, setPendingPermCodes] = useState(new Set());
  const saveTimersRef = useRef({});
  // 테넌트별 위젯 연결(2026-10-24): global = 기본 틀(운영사) / tenant = 그 테넌트 설정(손댄 칸만 따로 저장)
  const [scope, setScope] = useState({ mode: 'global', tenant: null, companies: [], hqCode: null });
  const [overridden, setOverridden] = useState(new Set());
  const nameOf = useCompanyNames(scope.companies);
  // 표준 헤더(stdHeaderRow) 반응형 여백용 — 디자인 표준 통일 (2026-08-27)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function load() {
    setLoading(true);
    const res = await apiClient.get('/api/admin/situation-widgets/catalog');
    if (!res.ok || res.data?.success === false) {
      showToast(res.data?.message || '상황판 위젯 설정을 불러오지 못했습니다.', 'error');
      setLoading(false);
      return;
    }
    const nextPermissions = Array.isArray(res.data.permissions) ? res.data.permissions : [];
    const nextConnections = Array.isArray(res.data.connections) ? res.data.connections : [];
    const nextWidgets = Array.isArray(res.data.widgets) ? res.data.widgets : [];
    setWidgets(nextWidgets);
    setPermissions(nextPermissions);
    setConnections(nextConnections);
    setScope({
      mode: res.data.mode === 'tenant' ? 'tenant' : 'global',
      tenant: res.data.tenant || null,
      companies: Array.isArray(res.data.companies) ? res.data.companies : [],
      hqCode: res.data.hq_code || null,
    });
    setOverridden(new Set(Array.isArray(res.data.overridden) ? res.data.overridden : []));
    const first = selectedPerm || nextPermissions[0]?.code || '';
    setSelectedPerm(first);
    setSelectedCodes(nextConnections.filter((row) => row.perm_code === first).map((row) => row.widget_code));
    // 매트릭스 map 초기화
    const nextMap = {};
    nextConnections.forEach((row) => {
      if (!nextMap[row.perm_code]) nextMap[row.perm_code] = [];
      nextMap[row.perm_code].push(row.widget_code);
    });
    matrixMapRef.current = nextMap;
    setMatrixMap(nextMap);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectionMap = useMemo(() => {
    const map = {};
    connections.forEach((row) => {
      if (!map[row.perm_code]) map[row.perm_code] = [];
      map[row.perm_code].push(row.widget_code);
    });
    return map;
  }, [connections]);

  function selectPermission(code) {
    setSelectedPerm(code);
    setSelectedCodes(connectionMap[code] || []);
  }

  function toggleWidget(code) {
    setSelectedCodes((prev) => (
      prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code]
    ));
  }

  async function save(acknowledgedSensitive = false) {
    if (!selectedPerm) return;
    setSaving(true);
    const res = await apiClient.put('/api/admin/situation-widgets/connections', {
      perm_code: selectedPerm,
      widget_codes: selectedCodes,
      acknowledged_sensitive: acknowledgedSensitive,
    });
    if (res.status === 409 && res.data?.requires_ack) {
      const message = (res.data.warnings || [])
        .map((item) => `${item.widget_name}: ${item.warning_text}`)
        .join('\n');
      const ok = window.confirm(`${res.data.message}\n\n${message}\n\n계속 연결할까요?`);
      setSaving(false);
      if (ok) return save(true);
      return;
    }
    if (!res.ok || res.data?.success === false) {
      showToast(res.data?.message || '저장에 실패했습니다.', 'error');
      setSaving(false);
      return;
    }
    showToast('상황판 위젯 연결을 저장했습니다.', 'success');
    await load();
    setSaving(false);
  }

  // 매트릭스 셀 토글: 즉시 UI 반영 + 디바운스 저장 (500ms).
  // 같은 perm 의 연속 변경은 마지막만 PUT 으로 합침.
  function handleMatrixToggle(permCode, widgetCode, checked) {
    const cur = new Set(matrixMapRef.current[permCode] || []);
    if (checked) cur.add(widgetCode);
    else cur.delete(widgetCode);
    matrixMapRef.current = { ...matrixMapRef.current, [permCode]: Array.from(cur) };
    setMatrixMap(matrixMapRef.current);
    if (saveTimersRef.current[permCode]) {
      clearTimeout(saveTimersRef.current[permCode]);
    }
    saveTimersRef.current[permCode] = setTimeout(() => {
      saveMatrixPerm(permCode);
    }, 500);
  }

  async function saveMatrixPerm(permCode, acknowledgedSensitive = false) {
    setPendingPermCodes((prev) => {
      const next = new Set(prev);
      next.add(permCode);
      return next;
    });
    // 최신 칸 상태에서 바로 꺼낸다(ref — 화면 갱신을 기다리지 않음)
    const codesToSave = matrixMapRef.current[permCode] || [];
    const res = await apiClient.put('/api/admin/situation-widgets/connections', {
      perm_code: permCode,
      widget_codes: codesToSave,
      acknowledged_sensitive: acknowledgedSensitive,
    });
    if (res.status === 409 && res.data?.requires_ack) {
      const message = (res.data.warnings || [])
        .map((item) => `${item.widget_name}: ${item.warning_text}`)
        .join('\n');
      const ok = window.confirm(`${res.data.message}\n\n${message}\n\n계속 연결할까요?`);
      if (ok) {
        await saveMatrixPerm(permCode, true);
        return;
      }
      // 사용자 취소 → 서버 상태로 롤백
      setPendingPermCodes((prev) => {
        const next = new Set(prev);
        next.delete(permCode);
        return next;
      });
      await load();
      return;
    }
    setPendingPermCodes((prev) => {
      const next = new Set(prev);
      next.delete(permCode);
      return next;
    });
    if (!res.ok || res.data?.success === false) {
      showToast(res.data?.message || '저장 실패', 'error');
      await load();
      return;
    }
    // 성공: connections state 도 갱신 → 다음 토글 시 비교 base 정확
    setConnections((prev) => {
      const filtered = prev.filter((row) => row.perm_code !== permCode);
      const added = codesToSave.map((wc) => ({ perm_code: permCode, widget_code: wc }));
      return [...filtered, ...added];
    });
    // 테넌트 화면: 서버가 '기본과 같아져 기본을 따름'인지 알려 준다 → '바꿈' 표시 갱신
    if (typeof res.data?.follows_default === 'boolean') {
      setOverridden((prev) => {
        const next = new Set(prev);
        if (res.data.follows_default) next.delete(permCode);
        else next.add(permCode);
        return next;
      });
    }
  }

  // 테넌트 화면: 고친 칸을 기본 설정으로 되돌림
  async function resetPerm(perm) {
    if (!window.confirm(`'${perm.label}' 칸을 기본 설정으로 되돌릴까요?`)) return;
    setPendingPermCodes((prev) => new Set(prev).add(perm.code));
    const res = await apiClient.put('/api/admin/situation-widgets/connections', {
      perm_code: perm.code, widget_codes: [], reset: true,
    });
    setPendingPermCodes((prev) => {
      const next = new Set(prev);
      next.delete(perm.code);
      return next;
    });
    if (!res.ok || res.data?.success === false) showToast(res.data?.message || '되돌리지 못했습니다.', 'error');
    await load();
  }

  const subtitle = scope.mode === 'tenant'
    ? `${scope.tenant?.name || '테넌트'} 설정 · 손대지 않은 칸은 기본 설정을 따릅니다.`
    : '기본 설정 · 운영사 직원과, 각 테넌트가 손대지 않은 칸에 쓰입니다.';

  return (
    // 상단 여백은 표준 헤더(stdHeaderRow)가 처리 — p-4 → px-4 pb-4 (2026-08-27 디자인 표준 통일)
    // 휴대폰은 공통 틀(AdminLayout main)이 좌우 16px 을 준다 — 겹치지 않게 0 (2026-09-28)
    <div className={`mx-auto ${isMobile ? 'px-0' : 'px-4'} pb-4 grid grid-cols-1 gap-4 max-w-[1440px] lg:max-w-none lg:w-full min-w-0`}>
      {/* 직원 관리 기준 표준 헤더 — 카드 박스 제거 (2026-08-27 디자인 표준 통일) */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>상황판 관리</h2>
          <div style={stdPageSubtitle}>{loading ? '상황판 위젯 노출을 관리합니다.' : subtitle}</div>
        </div>
        {/* 모바일에서만 노출되는 저장 버튼 (PC 매트릭스는 즉시 저장) */}
        <button
          type="button"
          onClick={() => save(false)}
          disabled={saving || loading || !selectedPerm}
          className="lg:hidden inline-flex items-center gap-2 disabled:opacity-50"
          style={stdBlackBtn}
        >
          <Save size={16} />
          {saving ? '저장 중' : '저장'}
        </button>
      </div>

      {/* PC: 매트릭스 view */}
      <div className="hidden lg:block min-w-0 w-full">
        {loading ? (
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-6 text-sm text-[var(--text-sub)]">
            불러오는 중...
          </div>
        ) : (
          <SituationWidgetMatrix
            widgets={widgets}
            permissions={permissions}
            connectionMap={matrixMap}
            onToggleCell={handleMatrixToggle}
            pendingPermCodes={pendingPermCodes}
            companies={scope.companies}
            hqCode={scope.hqCode}
            nameOf={nameOf}
            mode={scope.mode}
            overridden={scope.mode === 'tenant' ? overridden : null}
            onResetPerm={scope.mode === 'tenant' ? resetPerm : null}
          />
        )}
      </div>

      {/* 모바일/태블릿: 기존 좌측 권한 list + 우측 위젯 list */}
      <div className="lg:hidden">
        <SituationWidgetMobileList
          loading={loading}
          widgets={widgets}
          permissions={permissions}
          connectionMap={connectionMap}
          selectedPerm={selectedPerm}
          selectedCodes={selectedCodes}
          onSelectPermission={selectPermission}
          onToggleWidget={toggleWidget}
        />
      </div>
    </div>
  );
}
